/**
 * @file   MatchDemandPanel.js
 * @brief  Orchestrates the demand insight display on Match tab.
 *         Uses detectTier() to determine what to show based on match count:
 *         Tier 1: histogram + slider + stats + insight
 *         Tier 2: stats strip + insight
 *         Tier 3-4: LowDataState
 */
import React, { useState, useMemo, useRef } from "react";
import { detectTier } from "@/helpers/matchDemandUtils";
import { calculatePropertyPriceRange } from "@/helpers/priceCapUtils";
import { BUY, LONG_RENT } from "@/constants/common";
import DemandHistogram from "../DemandHistogram";
import SinglePriceSlider from "../SinglePriceSlider";
import QuickStatsStrip from "../QuickStatsStrip";
import InsightCallout from "../InsightCallout";
import LowDataState from "../LowDataState";

/**
 * Extract prices from match results for histogram computation.
 * Uses average of Budget_mn/Budget_mx when both exist.
 */
const extractPrices = (adList) => {
  if (!adList || adList.length === 0) return [];
  return adList
    .map((ad) => {
      const mn = ad.Budget_mn;
      const mx = ad.Budget_mx;
      if (mn && mx) return (mn + mx) / 2;
      if (mn) return mn;
      if (mx) return mx;
      return null;
    })
    .filter((p) => p != null && p > 0);
};

const DEBOUNCE_MS = 400;

const MatchDemandPanel = ({
  selectedProperty,
  adType,
  totalCountMatch,
  adListGitchaGenie,
  onPricePointChange,
}) => {
  const [sliderPrice, setSliderPrice] = useState(null);
  const [confirmedPrice, setConfirmedPrice] = useState(null);
  const debounceRef = useRef(null);

  const handleSliderChange = (price) => {
    setSliderPrice(price);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setConfirmedPrice(price);
      onPricePointChange?.(price);
    }, DEBOUNCE_MS);
  };

  const matchCount = totalCountMatch || 0;
  const tier = detectTier(matchCount);

  // Get asking price from selected property
  const askingPrice = useMemo(() => {
    if (!selectedProperty) return 0;
    if (adType === BUY) return selectedProperty.AskingPrice_Sell || 0;
    if (adType === LONG_RENT) return selectedProperty.AskingPrice_Rent || 0;
    return selectedProperty.AskingPrice_Night || 0;
  }, [selectedProperty, adType]);

  // Calculate slider range from property price
  const priceRange = useMemo(
    () => calculatePropertyPriceRange(askingPrice, adType),
    [askingPrice, adType],
  );

  // Extract match prices for histogram
  const prices = useMemo(
    () => extractPrices(adListGitchaGenie),
    [adListGitchaGenie],
  );

  const rangeMin = priceRange?.min || 0;
  const rangeMax = priceRange?.max || 0;

  // Reset slider when property changes
  const propertyId = selectedProperty?.Inv_Id;
  useMemo(() => {
    setSliderPrice(null);
    setConfirmedPrice(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, [propertyId]);

  // Tier 3-4: low data states
  if (tier >= 3) {
    return <LowDataState tier={tier} matchCount={matchCount} />;
  }

  // Tier 2: simplified - stats + insight, no histogram
  if (tier === 2) {
    return (
      <div className="match-demand-panel">
        <QuickStatsStrip
          matchCount={matchCount}
          prices={prices}
          adType={adType}
          rangeMax={rangeMax}
        />
        <InsightCallout
          askingPrice={askingPrice}
          sliderPrice={null}
          prices={prices}
          matchCount={matchCount}
          adType={adType}
        />
      </div>
    );
  }

  // Tier 1: full experience
  return (
    <div className="match-demand-panel">
      <QuickStatsStrip
        matchCount={matchCount}
        prices={prices}
        adType={adType}
        rangeMax={rangeMax}
      />
      {rangeMax > 0 && (
        <>
          <DemandHistogram
            prices={prices}
            adType={adType}
            askingPrice={askingPrice}
            sliderPrice={sliderPrice}
            rangeMin={rangeMin}
            rangeMax={rangeMax}
          />
          <SinglePriceSlider
            value={sliderPrice ?? askingPrice}
            onChange={handleSliderChange}
            min={rangeMin}
            max={rangeMax}
            step={priceRange?.step}
            askingPrice={askingPrice}
          />
        </>
      )}
      <InsightCallout
        askingPrice={askingPrice}
        sliderPrice={confirmedPrice}
        prices={prices}
        matchCount={matchCount}
        adType={adType}
      />
    </div>
  );
};

export default MatchDemandPanel;
